import React, { useEffect, useState } from 'react';
import { clearConsent, isAnalyticsConfigured } from '../../utils/analytics';

// Social links configuration - Update these URLs with your actual social media links
const SOCIAL_LINKS = {
  x: 'https://x.com/wallstreetle',
  tiktok: 'https://tiktok.com/@wallstreetle',
  reddit: 'https://reddit.com/r/wallstreetle',
  discord: 'https://discord.gg/8xnYDataFw'
};

interface SocialLinkProps {
  href: string;
  icon: string;
  alt: string;
}

const SocialLink: React.FC<SocialLinkProps> = ({ href, icon, alt }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-block transition-opacity hover:opacity-70"
    aria-label={alt}
  >
    <img
      src={icon}
      alt={alt}
      className="h-6 w-6 sm:h-7 sm:w-7"
    />
  </a>
);

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  // Decided after mount so pre-rendered HTML hydrates cleanly.
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  useEffect(() => {
    setShowCookieSettings(isAnalyticsConfigured());
  }, []);

  return (
    <footer
      className="w-full border-t border-gray-300 dark:border-border bg-background/95 backdrop-blur"
      role="contentinfo"
    >
      <div className="container mx-auto max-w-screen-xl px-6 sm:px-8 pt-6 pb-[5.25rem] sm:pt-8 sm:pb-[7.5rem]">
        {/* Top section: legal text and social links share a row on larger screens */}
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-center text-xs text-muted-foreground sm:text-left">
            <p className="text-xs font-medium sm:text-sm text-foreground">
              © {currentYear} Wallstreetle. All rights reserved.
            </p>
            <p className="mt-0.5">
              <a
                href="/policies/terms"
                className="text-primary hover:underline"
              >
                Terms & Conditions
              </a>
              {' '}|{' '}
              <a
                href="/policies/privacy"
                className="text-primary hover:underline"
              >
                Privacy Policy
              </a>
              {' '}|{' '}
              <a
                href="/support"
                className="text-primary hover:underline"
              >
                Support
              </a>
              {' '}|{' '}
              Contact us:{' '}
              <a
                href="mailto:info@wallstreetle.com"
                className="text-primary hover:underline"
              >
                info@wallstreetle.com
              </a>
              {' '}|{' '}
              <a
                href="/feedback"
                className="text-primary hover:underline"
              >
                Feedback
              </a>
              {' '}|{' '}
              <a
                href="/answers"
                className="text-primary hover:underline"
              >
                Yesterday&apos;s answers
              </a>
              {showCookieSettings && (
                <>
                  {' '}|{' '}
                  <button
                    type="button"
                    onClick={clearConsent}
                    className="text-primary hover:underline"
                  >
                    Cookie settings
                  </button>
                </>
              )}
            </p>
          </div>

          <div className="flex items-center gap-4 sm:gap-5">
            <SocialLink
              href={SOCIAL_LINKS.x}
              icon="/assets/social-assets/x-social-logo.svg"
              alt="Follow us on X (Twitter)"
            />
            <SocialLink
              href={SOCIAL_LINKS.tiktok}
              icon="/assets/social-assets/tiktok-social-logo.svg"
              alt="Follow us on TikTok"
            />
            <SocialLink
              href={SOCIAL_LINKS.reddit}
              icon="/assets/social-assets/reddit-sign-in.svg"
              alt="Join us on Reddit"
            />
            <SocialLink
              href={SOCIAL_LINKS.discord}
              icon="/assets/social-assets/discord-social-logo.svg"
              alt="Chat with us on Discord"
            />
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-6 border-t border-border pt-4">
          <p className="text-xs text-muted-foreground text-center leading-relaxed sm:text-sm">
            Wallstreetle is for entertainment and educational purposes only and does not constitute financial advice.
            Always consult a licensed financial advisor for investment guidance specific to your personal situation.
            Data accuracy is not guaranteed and cannot be relied on to make investment decisions.
            Always conduct your own research before making investment decisions.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
